<template>
  <div v-if="campaignMetrics && !loading" class="general-results-container d-flex flex-column">
    <span class="general-results-title">Resultados gerais</span>
    <v-row>
      <v-col v-for="card in metricsValues" :key="card.text">
        <cbm-card
          :number-text="card.value"
          :title="card.text"
          :tooltip="card.helpText"
          :is-currency="card.isCurrency"
          :boolean-loader="loadingFilter"
          :percent-text="card.underText"
          :percent-description="card.description"
          unset-percentage-colors
        />
      </v-col>
    </v-row>
    <v-card style="padding: 16px 20px 32px 20px" class="mt-5">
      <mf-funnel-graph
        full-width
        :labels="labels"
        :height="height"
        :width="width"
        :data-set-num="dataSetNum"
        :sub-label-value="subLabelValue"
        :values="funnelChartMetrics"
        :colors="colors"
        :direction="direction"
        :loading="loading"
        :gradient-direction="gradientDirection"
      >
        <template v-slot:quick-action="{ index }">
          <mf-quick-actions v-if="quickActionsProps[index]" v-bind="quickActionsProps[index]" />
        </template>
      </mf-funnel-graph>
    </v-card>
  </div>
</template>

<script>
import CbmCard from '@/components/CbmCard.vue'
import { DateTime } from 'luxon'
import { couponDiscount } from '../../../mixins/coupon-discount'
import { localeString } from '@/config/helpers.js'
import { criteriaGroup, redeemedCouponsCriteria, consumedCouponsCriteria } from '@/components/quick-actions/segmentationGroups.js'

export default {
  name: 'CouponGeneralResults',
  components: { CbmCard },
  mixins: [couponDiscount],
  props: {
    quickActions: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    benefitMetrics: null,
    benefitCampaign: null,
    labels: ['Clientes que colocaram o Cupom na carteira', 'Clientes que ativaram o Cupom de desconto', 'Clientes que consumiram o Cupom de desconto'],
    height: 300,
    width: 800,
    dataSetNum: 1,
    subLabelValue: 'percent',
    direction: 'horizontal',
    gradientDirection: 'horizontal',
    colors: [['#09535C', '#158372']],
    loadingFilter: false,
    loading: true,
    revenuePercentage: '',
  }),
  computed: {
    quickActionsProps() {
      let products = this.campaign.productsEnabled ? this.campaign.products.map((product) => product.sku) : []
      let groupProducts = []
      this.campaign?.productsGroups &&
        this.campaign.productsGroups.map((group) => {
          group.products.map((product) => groupProducts.push(product.sku))
        })
      products = products.concat(groupProducts)
      const unitsConfiguration = this.campaign.unitsInfo.map((unit) => {
        return { value: unit.id_erp, dataType: ['PHYSICAL', 'ECOMMERCE'] }
      })
      return [
        {
          key: `clients-redeemed-coupon`,
          quickActionGroupId: `clients-redeemed-coupon`,
          shortDescription: 'Clientes que colocaram o Cupom na carteira',
          longDescription: 'Clientes que colocaram o Cupom na carteira',
          segmentationGroup: () => criteriaGroup([redeemedCouponsCriteria(2, [this.campaign._id])]),
        },
        {
          key: `clients-activated-coupon`,
          quickActionGroupId: `clients-activated-coupon`,
          shortDescription: 'Clientes que ativaram o Cupom de desconto',
          longDescription: 'Clientes que ativaram o Cupom de desconto',
          segmentationGroup: () => criteriaGroup([redeemedCouponsCriteria(1, [this.campaign._id])]),
        },
        {
          key: `clients-consumed-coupon`,
          quickActionGroupId: `clients-consumed-coupon`,
          shortDescription: 'Clientes que consumiram o Cupom de desconto',
          longDescription: 'Clientes que consumiram o Cupom de desconto',
          segmentationGroup: () => criteriaGroup([consumedCouponsCriteria([this.campaign._id], unitsConfiguration, products, false)]),
        },
      ]
    },
    campaignMetrics: {
      get() {
        return this.benefitMetrics
      },
      set(value) {
        this.benefitMetrics = value
      },
    },
    campaign: {
      get() {
        return this.benefitCampaign
      },
      set(value) {
        this.benefitCampaign = value
      },
    },
    funnelChartMetrics() {
      return [
        [this.campaignMetrics.clientsMetrics.redeemedBenefits],
        [this.campaignMetrics.clientsMetrics.activatedBenefits],
        [this.campaignMetrics.clientsMetrics.consumedBenefits],
      ]
    },
    metricsValues() {
      return [
        {
          value: localeString(this.metrics.benefitGeneralMetrics.revenue || 0, 0),
          text: 'Faturamento',
          isCurrency: true,
          isInteger: false,
          underText: localeString(this.revenuePercentage || 0, 0) + '%',
          description: `do faturamento total`,
          helpText: 'Faturamento dos cupons fiscais em que os cupons de desconto foram utilizados.',
        },
        {
          value: localeString(this.metrics.benefitGeneralMetrics.consumedCoupons || 0),
          text: 'Cupons de desconto consumidos',
          isCurrency: false,
          isInteger: true,
          description: `limite de cupons da campanha`,
          underText: `${this.campaign.limitUse}`,
        },
        {
          value: localeString(this.metrics.benefitGeneralMetrics.discountGranted || 0, 0),
          text: 'Desconto concedido',
          isCurrency: true,
          isInteger: false,
          helpText: 'Total de desconto concedido pelo consumo dos cupons de desconto.',
        },
        {
          value: localeString(this.metrics.benefitGeneralMetrics.averageTicket || 0, 0),
          text: 'Ticket médio',
          isCurrency: true,
          isInteger: false,
          helpText: 'Ticket médio dos cupons fiscais em que os cupons de desconto foram utilizados.',
        },
      ]
    },
  },
  watch: {},
  mounted() {
    this.campaignMetrics = this.metrics
    this.campaign = this.benefit
    this.getRevenuePercentage()
  },
  methods: {
    localeString,
    formatDate(date) {
      return DateTime.fromFormat(date.substring(0, 10), 'yyyy-MM-dd').toFormat('yyyyMMdd')
    },
    getRevenuePercentage() {
      if (this.campaign) {
        this.loading = true
        const startDate = this.formatDate(this.campaign?.startDate)
        const endDate = this.formatDate(this.campaign?.expiresAt)
        const date = [
          startDate,
          DateTime.fromFormat(endDate, 'yyyyMMdd')
            .plus({ days: this.campaign?.daysToUseBenefitAfterRegistration || 0 })
            .toFormat('yyyyMMdd'),
        ]
        this.getRevenue({ date }).then((value) => {
          this.revenuePercentage = value ? (this.campaignMetrics?.benefitGeneralMetrics?.revenue / value) * 100 : 0
        })
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.general-results-container {
  .general-results-title {
    font-size: 22px;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.22px;
    color: $cinza-d10;
  }
}
</style>
